import React, { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useAppData } from '../../context/AppContext'
import { timesheetService, WeeklyTimesheetEntry, TimesheetEntryUI } from '../../services/timesheetService'
import { taskService, Task } from '../../services/taskService'
import { Plus, Trash2, ChevronLeft, ChevronRight } from '../icons/Icons'
import s from './shared.module.css'

interface TimesheetRow {
  id: string
  project_id: string
  task_id?: string
  project_name: string
  task_name?: string
  monday: number
  tuesday: number
  wednesday: number
  thursday: number
  friday: number
  saturday: number
  sunday: number
  is_billable: boolean
  description?: string
  monday_comment?: string
  tuesday_comment?: string
  wednesday_comment?: string
  thursday_comment?: string
  friday_comment?: string
  saturday_comment?: string
  sunday_comment?: string
}

type PeriodStatus = 'open' | 'submitted' | 'approved' | 'rejected' | null

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export default function WeekTimesheet() {
  const { organization, user } = useAuth()
  const { projects } = useAppData()
  const orgId = organization!.id

  const [weekStartDate, setWeekStartDate] = useState(() => {
    return timesheetService.getWeekStartDate(new Date())
  })
  const [rows, setRows] = useState<TimesheetRow[]>([])
  const [availableTasks, setAvailableTasks] = useState<{ [projectId: string]: Task[] }>({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [userResourceId, setUserResourceId] = useState<string | null>(null)
  const [periodStatus, setPeriodStatus] = useState<PeriodStatus>(null)
  const [rejectionReason, setRejectionReason] = useState<string>('')

  const isLocked = periodStatus === 'submitted' || periodStatus === 'approved'
  const isFutureWeek = (() => {
    const currentWeekStart = timesheetService.getWeekStartDate(new Date())
    return weekStartDate > currentWeekStart
  })()

  useEffect(() => {
    loadTimesheetData()
  }, [weekStartDate])

  const loadTimesheetData = async () => {
    setLoading(true)
    setPeriodStatus(null)
    setRejectionReason('')
    try {
      const weekDates = timesheetService.getWeekDates(weekStartDate)
      const entries = await timesheetService.getTimesheetEntries(
        user!.id,
        weekDates[0],
        weekDates[6]
      )

      const groupedByProject: { [key: string]: { [taskBillableKey: string]: TimesheetEntryUI[] } } = {}
      entries.forEach(entry => {
        const key = entry.project_id
        if (!groupedByProject[key]) groupedByProject[key] = {}

        const taskKey = entry.task_id || 'no-task'
        const billableKey = entry.is_billable ? 'billable' : 'nonbillable'
        const compositeKey = `${taskKey}|${billableKey}`
        if (!groupedByProject[key][compositeKey]) groupedByProject[key][compositeKey] = []
        groupedByProject[key][compositeKey].push(entry)
      })

      const newRows: TimesheetRow[] = []
      for (const projectId in groupedByProject) {
        for (const compositeKey in groupedByProject[projectId]) {
          const taskEntries = groupedByProject[projectId][compositeKey]
          const project = projects.find(p => p.id === projectId)

          const [taskPart, billablePart] = compositeKey.split('|')
          const taskName = taskPart !== 'no-task'
            ? (availableTasks[projectId]?.find(t => t.id === taskPart)?.title || '')
            : ''
          const row: TimesheetRow = {
            id: `${projectId}-${compositeKey}`,
            project_id: projectId,
            task_id: taskPart === 'no-task' ? undefined : taskPart,
            project_name: project?.title || 'Unknown',
            task_name: taskName,
            monday: 0,
            tuesday: 0,
            wednesday: 0,
            thursday: 0,
            friday: 0,
            saturday: 0,
            sunday: 0,
            is_billable: billablePart === 'billable',
            description: taskEntries[0]?.description,
            monday_comment: '',
            tuesday_comment: '',
            wednesday_comment: '',
            thursday_comment: '',
            friday_comment: '',
            saturday_comment: '',
            sunday_comment: ''
          }

          taskEntries.forEach(entry => {
            // Parse as local date components to avoid UTC-midnight timezone shift
            const [ey, em, ed] = entry.entry_date.split('T')[0].split('-').map(Number)
            const entryDate = new Date(ey, em - 1, ed)
            const dayIndex = (entryDate.getDay() + 6) % 7
            const dayName = DAYS[dayIndex] as keyof TimesheetRow
            const commentKey = `${dayName}_comment` as keyof TimesheetRow
            if (dayName in row && typeof row[dayName] === 'number') {
              (row as any)[dayName] = entry.hours
            }
            if (commentKey in row && entry.description) {
              (row as any)[commentKey] = entry.description
            }
          })

          newRows.push(row)
        }
      }

      setRows(newRows)

      // Fetch task names for any rows where task_name is empty but task_id is set
      const missingTaskIds = newRows.filter(r => r.task_id && !r.task_name).map(r => r.task_id!)
      if (missingTaskIds.length > 0) {
        const fetched: { [id: string]: string } = {}
        await Promise.all(missingTaskIds.map(async tid => {
          try {
            const task = await taskService.getTaskById(tid)
            if (task) fetched[tid] = task.title
          } catch { /* ignore */ }
        }))
        if (Object.keys(fetched).length > 0) {
          setRows(prev => prev.map(r => r.task_id && fetched[r.task_id] ? { ...r, task_name: fetched[r.task_id] } : r))
        }
      }

      // Fetch period status for this week
      try {
        const period = await timesheetService.getCurrentPeriod(orgId, weekStartDate)
        if (period) {
          setPeriodStatus(period.status as PeriodStatus)
          if (period.status === 'rejected' && period.rejected_reason) {
            setRejectionReason(period.rejected_reason)
          }
        } else {
          setPeriodStatus('open')
        }
      } catch {
        setPeriodStatus('open')
      }

      // Load only tasks assigned to the current user (single call, group by project)
      const userResource = await timesheetService.getResourceForUser(user!.id, orgId)
      if (userResource) {
        setUserResourceId(userResource.id)

        const allAssigned = await taskService.getAllAssignedTasks(orgId, userResource.id)
        const tasksMap: { [projectId: string]: Task[] } = {}
        for (const task of allAssigned) {
          if (task.status === 'done') continue
          if (!tasksMap[task.project_id]) tasksMap[task.project_id] = []
          tasksMap[task.project_id].push(task)
        }
        setAvailableTasks(tasksMap)
      } else {
        setUserResourceId(null)
        setAvailableTasks({})
      }
    } catch (error) {
      console.error('Failed to load timesheet data:', error)
    } finally {
      setLoading(false)
    }
  }

  const addRow = () => {
    const newRow: TimesheetRow = {
      id: `new-${Date.now()}`,
      project_id: '',
      task_id: undefined,
      project_name: '',
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0,
      is_billable: true,
      monday_comment: '',
      tuesday_comment: '',
      wednesday_comment: '',
      thursday_comment: '',
      friday_comment: '',
      saturday_comment: '',
      sunday_comment: ''
    }
    setRows([...rows, newRow])
  }

  const removeRow = (id: string) => {
    setRows(rows.filter(r => r.id !== id))
  }

  const updateRow = (id: string, field: keyof TimesheetRow, value: any) => {
    if (isLocked) return
    setRows(rows.map(row => {
      if (row.id === id) {
        const updated: TimesheetRow = { ...row, [field]: value }
        if (field === 'project_id') {
          const project = projects.find(p => p.id === value)
          updated.project_name = project?.title || ''
          updated.task_id = undefined
        }
        return updated
      }
      return row
    }))
  }

  const updateHours = (id: string, day: string, value: string) => {
    if (isLocked) return
    const hours = value === '' ? 0 : parseFloat(value)
    if (isNaN(hours) || !timesheetService.validateHours(hours)) return
    updateRow(id, day as keyof TimesheetRow, hours)
  }

  const getRowTotal = (row: TimesheetRow): number => {
    return DAYS.reduce((sum, day) => sum + (row[day as keyof TimesheetRow] as number || 0), 0)
  }

  const getDayTotal = (day: string): number => {
    return rows.reduce((sum, row) => sum + (row[day as keyof TimesheetRow] as number || 0), 0)
  }

  const getWeekTotal = (): number => {
    return rows.reduce((sum, row) => sum + getRowTotal(row), 0)
  }

  const handleSave = async () => {
    setSaving(true)
    setSaveError('')
    setSaveSuccess(false)

    try {
      if (!userResourceId) {
        throw new Error('User resource not found. Please ensure you are set up as a resource in the system.')
      }

      const validRows = rows.filter(r => r.project_id && getRowTotal(r) > 0)

      const entries: WeeklyTimesheetEntry[] = validRows.map(row => ({
        project_id: row.project_id,
        task_id: row.task_id,
        monday: row.monday,
        tuesday: row.tuesday,
        wednesday: row.wednesday,
        thursday: row.thursday,
        friday: row.friday,
        saturday: row.saturday,
        sunday: row.sunday,
        is_billable: row.is_billable,
        description: row.description,
        monday_comment: row.monday_comment,
        tuesday_comment: row.tuesday_comment,
        wednesday_comment: row.wednesday_comment,
        thursday_comment: row.thursday_comment,
        friday_comment: row.friday_comment,
        saturday_comment: row.saturday_comment,
        sunday_comment: row.sunday_comment
      }))

      await timesheetService.bulkUpsertWeeklyEntries(
        user!.id,
        userResourceId,
        orgId,
        weekStartDate,
        entries
      )

      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
      await loadTimesheetData()
    } catch (err: any) {
      setSaveError(err?.message || 'Failed to save timesheet')
    } finally {
      setSaving(false)
    }
  }

  const handleSubmit = async () => {
    if (!confirm('Submit timesheet for approval? You will not be able to edit it after submission.')) return

    setSubmitting(true)
    try {
      await handleSave()

      const period = await timesheetService.getCurrentPeriod(orgId, weekStartDate)
      if (period) {
        await timesheetService.submitTimesheetForPeriod(user!.id, period.id)
        alert('Timesheet submitted for approval!')
        await loadTimesheetData()
      } else {
        throw new Error('No active timesheet period found')
      }
    } catch (err: any) {
      alert(err?.message || 'Failed to submit timesheet')
    } finally {
      setSubmitting(false)
    }
  }

  const navigateWeek = (direction: 'prev' | 'next') => {
    const [y, m, d] = weekStartDate.split('-').map(Number)
    const date = new Date(y, m - 1, d + (direction === 'next' ? 7 : -7))
    const yy = date.getFullYear()
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const dd = String(date.getDate()).padStart(2, '0')
    setWeekStartDate(`${yy}-${mm}-${dd}`)
  }

  const goToCurrentWeek = () => {
    setWeekStartDate(timesheetService.getWeekStartDate(new Date()))
  }

  const getWeekDateRange = () => {
    const weekDates = timesheetService.getWeekDates(weekStartDate)
    const [sy, sm, sd] = weekDates[0].split('-').map(Number)
    const [ey2, em2, ed2] = weekDates[6].split('-').map(Number)
    const start = new Date(sy, sm - 1, sd)
    const end = new Date(ey2, em2 - 1, ed2)
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
  }

  const renderStatusBanner = () => {
    if (isFutureWeek) {
      return (
        <div style={{ padding: 12, marginBottom: 16, backgroundColor: '#fff4ce', border: '1px solid #ffe69c', borderRadius: 4, color: '#664d03', display: 'flex', alignItems: 'center', gap: 8 }}>
          <strong>Future Week</strong>
          <span style={{ fontWeight: 400 }}>— You cannot submit time for future weeks. Time can only be submitted up to today's date.</span>
        </div>
      )
    }
    if (periodStatus === 'submitted') {
      return (
        <div style={{ padding: 12, marginBottom: 16, backgroundColor: '#deecf9', border: '1px solid #b3d4f0', borderRadius: 4, color: '#004578', display: 'flex', alignItems: 'center', gap: 8 }}>
          <strong>Submitted for Approval</strong>
          <span style={{ fontWeight: 400 }}>— This timesheet is locked. You cannot edit it until it is approved or rejected.</span>
        </div>
      )
    }
    if (periodStatus === 'approved') {
      return (
        <div style={{ padding: 12, marginBottom: 16, backgroundColor: '#d1e7dd', border: '1px solid #badbcc', borderRadius: 4, color: '#0f5132', display: 'flex', alignItems: 'center', gap: 8 }}>
          <strong>Approved</strong>
          <span style={{ fontWeight: 400 }}>— This timesheet has been approved and is locked.</span>
        </div>
      )
    }
    if (periodStatus === 'rejected') {
      return (
        <div style={{ padding: 12, marginBottom: 16, backgroundColor: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 4, color: '#842029' }}>
          <strong>Rejected</strong>
          {rejectionReason && <span style={{ fontWeight: 400 }}> — {rejectionReason}</span>}
          <div style={{ marginTop: 4, fontSize: 13 }}>You can edit and resubmit this timesheet.</div>
        </div>
      )
    }
    return null
  }

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center' }}>Loading timesheet...</div>
  }

  const disabledStyle: React.CSSProperties = isLocked
    ? { opacity: 0.6, pointerEvents: 'none' as const, cursor: 'not-allowed' }
    : {}

  return (
    <div>
      <div className={s.pageHeader}>
        <div>
          <h2 className={s.pageTitle}>Weekly Timesheet</h2>
          <p className={s.pageSubtitle}>{getWeekDateRange()}</p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button className={s.secondaryBtn} onClick={() => navigateWeek('prev')}>
            <ChevronLeft size={16} /> Previous Week
          </button>
          <button className={s.secondaryBtn} onClick={goToCurrentWeek}>
            Current Week
          </button>
          <button className={s.secondaryBtn} onClick={() => navigateWeek('next')}>
            Next Week <ChevronRight size={16} />
          </button>
          <button className={s.primaryBtn} onClick={addRow} disabled={isLocked} style={isLocked ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}>
            <Plus size={14} /> Add Row
          </button>
        </div>
      </div>

      {renderStatusBanner()}

      {saveError && (
        <div style={{ padding: 12, marginBottom: 16, backgroundColor: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 4, color: '#842029' }}>
          {saveError}
        </div>
      )}

      {saveSuccess && (
        <div style={{ padding: 12, marginBottom: 16, backgroundColor: '#d1e7dd', border: '1px solid #badbcc', borderRadius: 4, color: '#0f5132' }}>
          Timesheet saved successfully!
        </div>
      )}

      <div className={s.card} style={{ overflowX: 'auto', ...disabledStyle }}>
        <table style={{ width: '100%', minWidth: 1200, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #dee2e6', minWidth: 200 }}>Project</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #dee2e6', minWidth: 150 }}>Task</th>
              <th style={{ padding: 12, textAlign: 'center', borderBottom: '2px solid #dee2e6', width: 80 }}>Billable</th>
              {DAY_LABELS.map((day, i) => {
                const weekDates = timesheetService.getWeekDates(weekStartDate)
                const [dy, dm, dd2] = weekDates[i].split('-').map(Number)
                const dateStr = weekDates[i]
                  ? new Date(dy, dm - 1, dd2).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : ''
                return (
                  <th key={day} style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: 80 }}>
                    <div>{day}</div>
                    <div style={{ fontSize: 11, fontWeight: 400, color: '#8a8888', marginTop: 2 }}>{dateStr}</div>
                  </th>
                )
              })}
              <th style={{ padding: 12, textAlign: 'center', borderBottom: '2px solid #dee2e6', width: 80, fontWeight: 700 }}>Total</th>
              <th style={{ padding: 12, textAlign: 'center', borderBottom: '2px solid #dee2e6', width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={12} style={{ padding: 40, textAlign: 'center', color: '#6c757d' }}>
                  No timesheet entries. Click "Add Row" to start.
                </td>
              </tr>
            )}
            {rows.map(row => (
              <React.Fragment key={row.id}>
                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: 8 }}>
                    <select
                      value={row.project_id}
                      onChange={(e) => updateRow(row.id, 'project_id', e.target.value)}
                      disabled={isLocked}
                      style={{ width: '100%', padding: 6, border: '1px solid #dee2e6', borderRadius: 4, ...(isLocked ? { backgroundColor: '#e9ecef' } : {}) }}
                    >
                      <option value="">Select Project...</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.title}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: 8 }}>
                    {isLocked ? (
                      <span style={{ fontSize: 13, color: '#605e5c', padding: '6px 0', display: 'block' }}>
                        {row.task_name || (row.task_id ? 'Loading...' : '—')}
                      </span>
                    ) : (
                      <select
                        value={row.task_id || ''}
                        onChange={(e) => updateRow(row.id, 'task_id', e.target.value || undefined)}
                        disabled={!row.project_id}
                        style={{ width: '100%', padding: 6, border: '1px solid #dee2e6', borderRadius: 4, ...(!row.project_id ? { backgroundColor: '#e9ecef' } : {}) }}
                      >
                        <option value="">None</option>
                        {row.project_id && availableTasks[row.project_id]?.map(task => (
                          <option key={task.id} value={task.id}>{task.title}</option>
                        ))}
                        {row.project_id && row.task_id && !availableTasks[row.project_id]?.some(t => t.id === row.task_id) && (
                          <option key={row.task_id} value={row.task_id} disabled>
                            {row.task_name || 'Task'} (Completed)
                          </option>
                        )}
                      </select>
                    )}
                  </td>
                  <td style={{ padding: 8, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={row.is_billable}
                      onChange={(e) => updateRow(row.id, 'is_billable', e.target.checked)}
                      disabled={isLocked}
                    />
                  </td>
                  {DAYS.map(day => {
                    const dayValue = row[day as keyof TimesheetRow]
                    return (
                      <td key={day} style={{ padding: 8 }}>
                        <input
                          type="number"
                          step="0.25"
                          min="0"
                          max="24"
                          value={typeof dayValue === 'number' ? dayValue || '' : ''}
                          onChange={(e) => updateHours(row.id, day, e.target.value)}
                          disabled={isLocked}
                        style={{
                          width: '100%',
                          padding: 6,
                          border: '1px solid #dee2e6',
                          borderRadius: 4,
                          textAlign: 'center',
                          fontFamily: 'monospace',
                          ...(isLocked ? { backgroundColor: '#e9ecef' } : {})
                        }}
                        placeholder="0"
                      />
                    </td>
                    )
                  })}
                  <td style={{ padding: 8, textAlign: 'center', fontWeight: 600, fontFamily: 'monospace' }}>
                    {getRowTotal(row).toFixed(2)}
                  </td>
                  <td style={{ padding: 8, textAlign: 'center' }}>
                    <button
                      className={`${s.actionBtn} ${s.del}`}
                      onClick={() => removeRow(row.id)}
                      disabled={isLocked}
                      title="Delete row"
                      style={isLocked ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <td colSpan={3} style={{ padding: 8, fontSize: 12, fontWeight: 600, color: '#6c757d' }}>
                    Comments
                  </td>
                  {DAYS.map(day => {
                    const commentKey = `${day}_comment` as keyof TimesheetRow
                    const commentValue = row[commentKey]
                    const hoursValue = row[day as keyof TimesheetRow]
                    const hasHours = typeof hoursValue === 'number' && hoursValue > 0
                    const commentDisabled = !hasHours || isLocked

                    return (
                      <td key={`${day}-comment`} style={{ padding: 8 }}>
                        <input
                          type="text"
                          value={typeof commentValue === 'string' ? commentValue : ''}
                          onChange={(e) => updateRow(row.id, commentKey, e.target.value)}
                          disabled={commentDisabled}
                          style={{
                            width: '100%',
                            padding: 6,
                            border: '1px solid #dee2e6',
                            borderRadius: 4,
                            fontSize: 12,
                            backgroundColor: commentDisabled ? '#e9ecef' : 'white'
                          }}
                          placeholder={hasHours ? 'Add comment...' : ''}
                        />
                      </td>
                    )
                  })}
                  <td colSpan={2}></td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 700 }}>
              <td colSpan={3} style={{ padding: 12, textAlign: 'right' }}>Daily Totals:</td>
              {DAYS.map(day => (
                <td key={day} style={{ padding: 12, textAlign: 'center', fontFamily: 'monospace' }}>
                  {getDayTotal(day).toFixed(2)}
                </td>
              ))}
              <td style={{ padding: 12, textAlign: 'center', fontFamily: 'monospace', fontSize: 16 }}>
                {getWeekTotal().toFixed(2)}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div style={{ marginTop: 16, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button
          className={s.secondaryBtn}
          onClick={handleSave}
          disabled={saving || rows.length === 0 || isLocked}
        >
          {saving ? 'Saving...' : 'Save Draft'}
        </button>
        {periodStatus !== 'submitted' && periodStatus !== 'approved' && (
          <button
            className={s.primaryBtn}
            onClick={handleSubmit}
            disabled={submitting || rows.length === 0 || getWeekTotal() === 0 || isFutureWeek}
            title={isFutureWeek ? 'Cannot submit a timesheet for a future week' : undefined}
          >
            {submitting ? 'Submitting...' : 'Submit for Approval'}
          </button>
        )}
      </div>

      <div style={{ marginTop: 24, padding: 16, backgroundColor: '#f8f9fa', borderRadius: 8 }}>
        <h3 style={{ marginBottom: 12, fontSize: 14, fontWeight: 600 }}>Instructions</h3>
        <ul style={{ fontSize: 13, color: '#6c757d', lineHeight: 1.6, paddingLeft: 20 }}>
          <li>Enter hours in 15-minute increments (0.25, 0.5, 0.75, 1.0, etc.)</li>
          <li>Select a project and optionally a task for each row</li>
          <li>Check "Billable" for hours that should be billed to the client</li>
          <li>Save your draft anytime to preserve your work</li>
          <li>Submit for approval when the week is complete</li>
          <li>Once submitted, you cannot edit the timesheet until it is approved or rejected</li>
        </ul>
      </div>
    </div>
  )
}
